// RoundAnnouncement — "Tell all candidates the next round dates": to every
// applicant of the drive (Sanjoy, 27 Oct 2026, the default) or only those who
// paid the fee (Shohini, 19 Oct), optionally one role's. Type the round details, preview the
// exact message with how many people it reaches and a masked list, then send
// from that preview only. One message per person. Nothing goes while the
// school's candidate messages are paused, or while Messages holds candidates.
// @story HIR-HR8
// @story HIR-HR9

import React, { useEffect, useState } from 'react';
import { Megaphone, Loader2, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '../../services/api';
import RolePicker from '../../components/RolePicker';

const FIELDS = [
  { key: 'assessmentDate', label: 'Online proctored assessment', placeholder: 'Saturday, 28th November 2026' },
  { key: 'interviewDates', label: 'Telephonic interviews', placeholder: '30th November to 5th December 2026' },
  { key: 'schoolName', label: 'School name', placeholder: 'Delhi Public School Pune East' },
  { key: 'hrName', label: 'Signed by (name)', placeholder: 'Debasmita Mukherjee' },
  { key: 'hrTitle', label: 'Their title', placeholder: 'HR Learning Lead' },
  { key: 'hrPhone', label: 'Their phone', placeholder: '+91 70301 20274' },
];

/* startOpen: opened straight away, as in the Dashboard's window (29 Oct 2026). */
export default function RoundAnnouncement({ startOpen = false } = {}) {
  // closed | loading | form | previewing | preview | sending | done | error
  const [st, setSt] = useState({ phase: 'closed' });
  const [round, setRound] = useState({});
  const [roleId, setRoleId] = useState('all');
  const [who, setWho] = useState('all');
  // Channels are ticked, never assumed (27 Oct 2026): email, WhatsApp, or
  // both. Email alone to start with; WhatsApp only when ticked.
  const [chan, setChan] = useState({ email: true, whatsapp: false });
  const channels = ['email', 'whatsapp'].filter((c) => chan[c]);
  // This school's own WhatsApp wording (28 Oct 2026): shown with Meta's
  // answer, and submitted to Meta by the person who presses the button.
  const [wa, setWa] = useState({ wording: null, busy: false, msg: '' });
  const loadWa = () => api.get('/dashboard/announce-round/whatsapp')
    .then((r) => setWa((w) => ({ ...w, wording: r.data.wording || null })))
    .catch(() => {});
  const submitWa = () => {
    setWa((w) => ({ ...w, busy: true, msg: '' }));
    api.post('/dashboard/announce-round/whatsapp/submit')
      .then((r) => { setWa((w) => ({ ...w, busy: false, msg: r.data.message })); loadWa(); })
      .catch((e) => setWa((w) => ({ ...w, busy: false, msg: e?.response?.data?.message || 'Could not submit to Meta.' })));
  };

  const open = () => {
    setSt({ phase: 'loading' });
    api.get('/dashboard/announce-round')
      .then((r) => {
        setRound(r.data.round || {});
        setWho(r.data.defaultWho || 'all');
        setSt({ phase: 'form', roles: r.data.roles || [], audiences: r.data.audiences || { all: r.data.people || 0, paid: 0 } });
        loadWa();
      })
      .catch(() => setSt({ phase: 'error', msg: 'Could not load the announcement.' }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (startOpen) open(); }, []);

  const doPreview = () => {
    setSt((s) => ({ ...s, phase: 'previewing', msg: '' }));
    api.post('/dashboard/announce-round/preview', { jobId: roleId, who, channels, round })
      .then((r) => setSt((s) => ({ ...s, phase: 'preview', preview: r.data })))
      .catch((e) => setSt((s) => ({ ...s, phase: 'form', msg: e?.response?.data?.message || 'Could not prepare the preview.' })));
  };

  const doSend = () => {
    const previewId = st.preview?.previewId;
    setSt((s) => ({ ...s, phase: 'sending' }));
    api.post('/dashboard/announce-round/send', { previewId })
      .then((r) => setSt({ phase: 'done', result: r.data }))
      .catch((e) => setSt((s) => ({ ...s, phase: 'preview', msg: e?.response?.data?.message || 'Sending failed. Nothing more was sent.' })));
  };

  // A test to a few staff first (Sanjoy, 27 Oct 2026): internal addresses
  // and the staff test mobiles only, never a candidate; the preview can still
  // be sent afterwards. A mobile gets the WhatsApp version once Meta approves.
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState({ busy: false, msg: '' });
  const doTest = () => {
    const to = testTo.split(/[,;]+/).map((x) => x.trim()).filter(Boolean)
      .map((x) => (x.includes('@')
        ? { email: x, name: x.split('@')[0].replace(/[._]/g, ' ') }
        : { phone: x.replace(/[^\d+]/g, ''), name: 'Colleague' }));
    setTesting({ busy: true, msg: '' });
    api.post('/dashboard/announce-round/test', { previewId: st.preview?.previewId, to })
      // Each person's reason too: "0 of 1" alone hid "held by Messages" (28 Oct).
      .then((r) => setTesting({ busy: false, msg: [r.data.message, ...(r.data.sent || []).filter((s) => s.problems?.length).map((s) => `${s.to}: ${s.problems.join('; ')}`)].join(' ') }))
      .catch((e) => setTesting({ busy: false, msg: e?.response?.data?.message || 'The test could not be sent.' }));
  };

  const set = (k) => (e) => setRound((v) => ({ ...v, [k]: e.target.value }));
  const p = st.preview;

  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow p-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-heading font-bold text-[15px]">Tell all candidates the next round dates</h2>
        {st.phase === 'closed' && (
          <button onClick={open}
            className="flex items-center gap-1.5 text-sm font-medium text-orange-600 border border-orange-500 rounded-dps px-3 py-1.5 hover:bg-orange-50 transition">
            <Megaphone size={14} /> Prepare the message
          </button>
        )}
        {st.phase === 'loading' && <span className="text-sm text-gray-500 flex items-center gap-1.5"><Loader2 size={14} className="animate-spin" /> Loading…</span>}
      </div>
      <p className="text-xs text-gray-500 mt-1">By email, by WhatsApp, or both — you tick which — once per person: all applicants, or only those who paid the fee. Anyone who already had it by that channel is skipped. You see the exact message and who gets it before anything is sent.</p>

      {st.phase === 'error' && <div className="text-sm text-red-600 mt-2">{st.msg}</div>}

      {['form', 'previewing'].includes(st.phase) && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <fieldset className="block text-gray-600 text-xs">
              <legend>Send by</legend>
              <div className="flex gap-4 mt-1.5 text-sm text-gray-800">
                {[['email', 'Email'], ['whatsapp', 'WhatsApp']].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-1.5">
                    <input type="checkbox" checked={chan[k]} onChange={(e) => setChan((c) => ({ ...c, [k]: e.target.checked }))} /> {label}
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="block text-gray-600 text-xs">Who
              <select value={who} onChange={(e) => setWho(e.target.value)} className="block w-full mt-0.5 border border-gray-300 rounded-dps px-2 py-1.5 text-sm bg-white">
                <option value="all">All applicants ({st.audiences?.all ?? 0} people)</option>
                <option value="paid">Paid only ({st.audiences?.paid ?? 0} people)</option>
              </select>
            </label>
            {/* Type to find a role (29 Oct 2026). */}
            <div className="block text-gray-600 text-xs">
              <RolePicker label="Role" roles={st.roles || []} allOption={{ value: 'all', label: 'Every role' }}
                value={roleId} onChange={setRoleId} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {FIELDS.map((f) => (
              <label key={f.key} className="block text-gray-600 text-xs">{f.label}
                <input value={round[f.key] || ''} onChange={set(f.key)} placeholder={f.placeholder}
                  className="block w-full mt-0.5 border border-gray-300 rounded-dps px-2 py-1.5 text-sm bg-white" />
              </label>
            ))}
          </div>
          {wa.wording && (
            <details className="rounded border border-gray-200 bg-white p-2 text-xs">
              <summary className="cursor-pointer text-gray-700">
                This school's WhatsApp wording: {wa.wording.ready ? 'approved by Meta, used for WhatsApp' : `Meta: ${wa.wording.status}`}
              </summary>
              <div className="text-gray-700 whitespace-pre-wrap mt-2 rounded border border-gray-100 p-2">{wa.wording.text}</div>
              <div className="text-gray-500 mt-1">{'{{1}}'} is each person's first name. It goes to Meta from the school's WhatsApp account; nothing is sent to anyone.</div>
              {wa.wording.reason && <div className="text-red-600 mt-1">Meta: {wa.wording.reason}</div>}
              {['not submitted', 'rejected'].includes(wa.wording.status) && (
                <button onClick={submitWa} disabled={wa.busy}
                  className="mt-2 flex items-center gap-1.5 border border-orange-400 text-orange-700 rounded-dps px-3 py-1 text-xs font-medium hover:bg-orange-50 disabled:opacity-50">
                  {wa.busy ? <Loader2 size={12} className="animate-spin" /> : null} Submit this wording to Meta
                </button>
              )}
              {wa.msg && <div className="text-gray-700 mt-1">{wa.msg}</div>}
            </details>
          )}
          {st.msg && <div className="text-red-600 text-xs">{st.msg}</div>}
          <div className="flex gap-2">
            <button onClick={doPreview} disabled={st.phase === 'previewing' || !channels.length}
              className="flex items-center gap-1.5 bg-orange-500 text-white rounded-dps px-3 py-1.5 text-sm font-medium hover:bg-orange-600 transition disabled:opacity-50">
              {st.phase === 'previewing' ? <><Loader2 size={14} className="animate-spin" /> Preparing…</> : 'Preview'}
            </button>
            <button onClick={() => setSt({ phase: 'closed' })} className="text-gray-600 rounded-dps px-3 py-1.5 text-sm hover:bg-gray-100 transition">Cancel</button>
          </div>
        </div>
      )}

      {['preview', 'sending'].includes(st.phase) && p && (
        <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm space-y-2">
          <div className="text-gray-800">
            This goes to <span className="font-bold">{p.counts.people}</span> {p.counts.people === 1 ? 'person' : 'people'} {p.who === 'paid' ? 'who applied and paid the fee' : 'who applied'}{p.role ? <> for <span className="font-medium">{p.role}</span></> : ''}:
            {' '}by <span className="font-bold">{p.channelText || 'email'}</span>
            {' '}({[p.counts.email ? `${p.counts.email} by email` : '', p.counts.whatsapp ? `${p.counts.whatsapp} by WhatsApp` : ''].filter(Boolean).join(', ')}).
            {p.counts.alreadySent ? <span className="text-gray-500"> {p.counts.alreadySent} who already had it that way are skipped.</span> : null}
            {p.counts.noAddress ? <span className="text-gray-500"> {p.counts.noAddress} who cannot be reached that way are left out.</span> : null}
          </div>
          {p.held && (
            <div className="flex gap-1.5 text-amber-800 text-xs"><AlertTriangle size={14} className="shrink-0" /> {p.heldMessage} Nothing can be sent until then.</div>
          )}
          <div className="text-xs text-gray-500">As {p.message.forName} will read it:</div>
          {p.message.email && (
            <div className="rounded border border-gray-200 bg-white p-2 text-xs">
              <div className="text-gray-500">Email</div>
              <div className="font-medium text-gray-800 mt-1">Subject: {p.message.email.subject}</div>
              <div className="text-gray-700 whitespace-pre-wrap mt-1 max-h-72 overflow-auto">{p.message.email.text}</div>
            </div>
          )}
          {p.message.whatsapp && (
            <div className="rounded border border-gray-200 bg-white p-2 text-xs">
              <div className="text-gray-500">WhatsApp{p.message.whatsapp.approved ? '' : ' — not sent yet'}</div>
              <div className="text-gray-700 whitespace-pre-wrap mt-1">{p.message.whatsapp.text}</div>
              {p.message.whatsapp.note && <div className="text-amber-700 mt-1">{p.message.whatsapp.note}</div>}
            </div>
          )}
          <div className="text-xs">
            <div className="text-gray-500 mb-1">Who gets it ({p.recipients.length}):</div>
            <div className="max-h-40 overflow-auto rounded border border-gray-200 bg-white divide-y divide-gray-100">
              {p.recipients.map((r, i) => (
                <div key={i} className="flex justify-between gap-2 px-2 py-1">
                  <span className="text-gray-700 truncate">{r.name}</span>
                  <span className="shrink-0 text-gray-400 font-mono">{[r.email, r.phone].filter(Boolean).join(' · ')}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="text-xs text-gray-500">{p.note}</div>
          <div className="rounded border border-gray-200 bg-white px-2 py-2 space-y-1.5">
            <label className="block text-gray-600 text-xs">Send a test first, to staff only (internal emails or staff test mobiles, separated by commas)
              <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="name@equaos.in, +91 98xxx xxxxx"
                className="block w-full mt-0.5 border border-gray-300 rounded-dps px-2 py-1.5 text-sm bg-white" />
            </label>
            <button onClick={doTest} disabled={testing.busy || !testTo.trim()}
              className="text-orange-700 border border-orange-300 rounded-dps px-3 py-1 text-xs font-medium hover:bg-orange-100 transition disabled:opacity-50">
              {testing.busy ? 'Sending the test…' : 'Send a test'}
            </button>
            {testing.msg && <div className="text-xs text-gray-700">{testing.msg}</div>}
          </div>
          {st.msg && <div className="text-red-600 text-xs">{st.msg}</div>}
          <div className="flex gap-2">
            <button onClick={doSend} disabled={st.phase === 'sending' || p.held}
              className="flex items-center gap-1.5 bg-orange-500 text-white rounded-dps px-3 py-1.5 text-sm font-medium hover:bg-orange-600 transition disabled:opacity-50">
              {st.phase === 'sending' ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send by {p.channelText || 'email'} to {p.counts.people}</>}
            </button>
            <button onClick={() => setSt((s) => ({ ...s, phase: 'form', preview: null, msg: '' }))} className="text-gray-600 rounded-dps px-3 py-1.5 text-sm hover:bg-gray-100 transition">Change</button>
            <button onClick={() => setSt({ phase: 'closed' })} className="text-gray-600 rounded-dps px-3 py-1.5 text-sm hover:bg-gray-100 transition">Cancel</button>
          </div>
        </div>
      )}

      {st.phase === 'done' && (
        <div className="mt-2 text-sm flex items-center justify-between gap-2">
          <span className={`flex items-center gap-1.5 ${st.result.status === 'sent' ? 'text-green-700' : 'text-amber-800'}`}>
            {st.result.status === 'sent' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />} {st.result.message}
          </span>
          <button onClick={() => setSt({ phase: 'closed' })} className="text-gray-600 text-sm hover:underline">Close</button>
        </div>
      )}
    </section>
  );
}
